import { FC, ReactElement } from 'react'
import { Text, View } from 'react-native'
import { IconIds } from '../Icon'
import { Tab } from '../Tab'
import styles from './styles'

export interface PageSelectorProps {
  onSelect?: (currentPage: number, numRecords: number) => void
  title?: (currentPage: number, totalRecords: number, numRecords: number, totalPages: number) => string
  totalRecords: number
  default: number
  recordsPerPage: number
  type?: PageSelectorType
  hide?: boolean
  totalPages?: number
}

enum ArrowTypes {
  Left = 'Left',
  Right = 'Right',
}

export interface ArrowButtonProps {
  leftButtonPressed: () => void
  rightButtonPressed: () => void
  lastPageIndex: () => number
  pageIndex: number
  defaultPage: number
  totalRecords: number
  type: ArrowTypes
  hide: boolean
}

export enum PageSelectorType {
  default = 'default',
}

const titleString = (currentPage: number, totalRecords: number, numRecords: number, totalPages: number): string => {
  let records = Math.min(currentPage * numRecords, totalRecords)
  let one = records === 0 ? 0 : 1
  if (currentPage === totalPages) {
    return `${totalRecords - numRecords + one}-${records} of ${totalRecords}`
  }
  return `${currentPage * numRecords - numRecords + one}-${records} of ${totalRecords}`
}

const ArrowButton = ({
  leftButtonPressed,
  rightButtonPressed,
  lastPageIndex,
  pageIndex,
  defaultPage,
  totalRecords,
  type,
  hide,
}: ArrowButtonProps): ReactElement => {
  if (hide) {
    return <></>
  }
  let onSelection = rightButtonPressed
  let iconId = IconIds.ARROW_RIGHT
  if (pageIndex === 1 && type === ArrowTypes.Left) {
    onSelection = leftButtonPressed
    iconId = IconIds.ARROW_LEFT
  }

  const checkDisable =
    totalRecords === 0 ||
    (defaultPage === 1 && iconId === IconIds.ARROW_LEFT) ||
    (lastPageIndex() === defaultPage && iconId === IconIds.ARROW_RIGHT)

  const checkTabState = () => {
    if (defaultPage === pageIndex) {
      return 'selected'
    }
    if (totalRecords === 0) {
      return 'selected'
    }
    return 'normal'
  }

  const tabState = checkTabState()

  return <Tab iconId={iconId} isDisabled={checkDisable} state={tabState} type={'default'} onSelected={onSelection} />
}

export const PageSelector: FC<PageSelectorProps> = ({
  title = titleString,
  onSelect,
  default: defaultPage,
  totalRecords,
  type = PageSelectorType.default,
  recordsPerPage,
  hide = false,
  totalPages = -1,
}: PageSelectorProps) => {
  const themedStyle = styles()

  if (hide) {
    return null
  }

  const lastPageIndex = (): number => {
    if (totalPages === defaultPage) {
      return defaultPage
    }
    let lastPage = Math.floor(totalRecords / recordsPerPage)
    if (totalRecords % recordsPerPage > 0) {
      lastPage += 1
    }
    return lastPage
  }

  const leftButtonPressed = (): void => {
    if (defaultPage > 1) {
      const index = defaultPage - 1
      if (onSelect) {
        onSelect(index, recordsPerPage)
      }
    }
  }

  const rightButtonPressed = (): void => {
    if (defaultPage < lastPageIndex()) {
      const index = defaultPage + 1
      if (onSelect) {
        onSelect(index, recordsPerPage)
      }
    }
  }

  const isSinglePage = totalRecords <= recordsPerPage
  const containerStyle = isSinglePage ? themedStyle[type].containerText : themedStyle[type].container
  const separatorStyle = isSinglePage ? themedStyle[type].seperatorText : themedStyle[type].seperator

  return (
    <View style={containerStyle}>
      <ArrowButton
        hide={isSinglePage}
        type={ArrowTypes.Left}
        pageIndex={1}
        leftButtonPressed={leftButtonPressed}
        rightButtonPressed={rightButtonPressed}
        lastPageIndex={lastPageIndex}
        defaultPage={defaultPage}
        totalRecords={totalRecords}
      />
      <View style={separatorStyle} />
      <View>
        <Text style={themedStyle[type].textStyle}>{title(defaultPage, totalRecords, recordsPerPage, totalPages)}</Text>
      </View>
      <View style={separatorStyle} />
      <ArrowButton
        hide={isSinglePage}
        type={ArrowTypes.Right}
        pageIndex={lastPageIndex()}
        leftButtonPressed={leftButtonPressed}
        rightButtonPressed={rightButtonPressed}
        lastPageIndex={lastPageIndex}
        defaultPage={defaultPage}
        totalRecords={totalRecords}
      />
    </View>
  )
}
